/**
 * Root-level React error boundary.
 *
 * Class component because that is still the only way React supports error
 * boundaries — there is no `useErrorBoundary` hook in stable React 19.
 *
 * Behaviour
 * ---------
 * - Catches any render-time exception in the component tree below it.
 * - Renders a minimal "something went wrong" fallback with a "Reload" button.
 * - Calls an optional `onError` callback so apps can ship the error to
 *   Application Insights via the browser SDK (M28).
 *
 * What this is NOT
 * ----------------
 * - It does NOT catch errors in event handlers, async code, or setTimeout
 *   callbacks. Those are React's documented limitations and apply to every
 *   error boundary in the React ecosystem. Use try/catch for those.
 * - It does NOT decode or display the actual error message. The user sees
 *   a generic message — same principle as the backend error envelope.
 *
 * Color-agnostic
 * --------------
 * The default styling uses neutral greys (Tailwind's `slate-*` palette,
 * which every app already has via the default Tailwind config). Apps that
 * want a branded fallback pass a custom `fallback` element instead.
 */

import React from 'react';

/**
 * @typedef {object} ErrorBoundaryProps
 * @property {React.ReactNode} children
 * @property {(error: Error, info: React.ErrorInfo) => void} [onError]
 * @property {React.ReactNode | ((error: Error, reset: () => void) => React.ReactNode)} [fallback]
 */

/**
 * @typedef {object} ErrorBoundaryState
 * @property {Error | null} error
 */

export class RootErrorBoundary extends React.Component {
  /** @type {ErrorBoundaryState} */
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Always log to console — apps usually have stricter logging in prod via
    // the App Insights SDK browser exporter, but the console line is the
    // fastest path to a stack trace during local dev.
    console.error('[bc-ui] Unhandled error caught by RootErrorBoundary:', error, info);
    if (this.props.onError) {
      try {
        this.props.onError(error, info);
      } catch (handlerError) {
        console.error('[bc-ui] onError callback threw:', handlerError);
      }
    }
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error === null) {
      return this.props.children;
    }

    if (typeof this.props.fallback === 'function') {
      return this.props.fallback(this.state.error, this.reset);
    }
    if (this.props.fallback) {
      return this.props.fallback;
    }

    return <DefaultFallback onReset={this.reset} />;
  }
}

/**
 * Minimal grey-on-white fallback. No brand colors, no fonts, no gradients —
 * just enough to explain to the user that something went wrong and offer
 * them an out. Apps with branded crash screens override `fallback`.
 */
function DefaultFallback({ onReset }) {
  return (
    <div
      role="alert"
      className="flex min-h-screen items-center justify-center bg-slate-50 p-6"
    >
      <div className="max-w-md w-full text-center">
        <h1 className="text-2xl font-semibold text-slate-900 mb-3">
          Something went wrong
        </h1>
        <p className="text-slate-600 mb-6">
          We hit an unexpected error. Try reloading — if it keeps happening,
          please report it.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            onClick={onReset}
            className="min-h-touch px-6 py-3 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="min-h-touch px-6 py-3 rounded-md bg-slate-900 text-white hover:bg-slate-800 transition-colors"
          >
            Reload page
          </button>
        </div>
      </div>
    </div>
  );
}
